import { track } from "@/lib/analytics"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { flushThreadEdits } from "./edits"
import { downloadOioSimDocx } from "./lib/exportDocx"
import { CAPTION_PLACEHOLDERS, canonicalIds, planNumbering, sectionHeadings, sectionLabel, stripLeadingHeading, todayDmy } from "./sections"
import { SectionEditor } from "./SectionEditor"
import { SIM_BANNER_COPY } from "./SimBanner"
import { useSimStore } from "./store"

/** Canonical section ids for the case, how many have text, and whether drafting has begun (any section exists, or one is streaming). */
export function useDraftSections(threadId: string) {
  const draft = useSimStore((s) => s.threads[threadId]?.draft ?? null)
  const streaming = useSimStore((s) => s.threads[threadId]?.streamingSection ?? null)
  const ids = useMemo(
    () => canonicalIds([...Object.keys(draft?.sections ?? {}), ...(streaming ? [streaming.sectionId] : [])]),
    [draft, streaming],
  )
  const drafted = ids.filter((id) => draft?.sections[id]?.markdown.trim()).length
  const started = Object.keys(draft?.sections ?? {}).length > 0 || streaming !== null
  return { draft, streaming, ids, drafted, started }
}

const collapsedKey = (threadId: string) => `osim-pane-collapsed:${threadId}`
export function readCollapsed(threadId: string): boolean {
  try { return window.localStorage.getItem(collapsedKey(threadId)) === "1" } catch { return false }
}
export function writeCollapsed(threadId: string, collapsed: boolean) {
  try { window.localStorage.setItem(collapsedKey(threadId), collapsed ? "1" : "0") } catch { /* storage unavailable */ }
}

const MIN_PCT = 35
const MAX_PCT = 70

interface Props {
  threadId: string
  title: string
  /** True at 1024px and wider: a side-by-side split. Below it the pane is full-screen. */
  wide: boolean
  /** Whether the pane is showing (not collapsed on wide screens; opened on narrow ones). */
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Width of the pane as a percentage of the window (wide only). */
  widthPct: number
  onWidthPct: (pct: number) => void
  /** Agent saved while the pane was closed. */
  unseen: boolean
}

/**
 * The editor half of the case screen: the sim banner, Download DOCX, an outline, and every section as one continuous
 * editable document (one SectionEditor each, so autosave, versions, conflicts, history and recovered edits are unchanged).
 * It stays mounted while collapsed so open editors keep their state; collapsed, it is inert and hidden and a slim tab is shown.
 */
export function DraftPane({ threadId, title, wide, open, onOpenChange, widthPct, onWidthPct, unseen }: Props) {
  const stale = useSimStore((s) => s.threads[threadId]?.staleStages)
  const { draft, streaming, ids, drafted } = useDraftSections(threadId)
  const { download, exporting, exportError } = useDraftExport(threadId, title)
  const local = useRef<HTMLElement | null>(null)
  const tab = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    const el = local.current
    if (!el) return
    if (open) el.removeAttribute("inert")
    else el.setAttribute("inert", "")
  }, [open])

  // Pending edits are saved before the pane hides; the editors stay mounted, so nothing is lost either way.
  const hide = () => {
    void flushThreadEdits(threadId)
    onOpenChange(false)
    requestAnimationFrame(() => tab.current?.focus({ preventScroll: true }))
  }

  const titles = draft?.titles

  const jump = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault()
    document.getElementById(`osim-sec-${id}`)?.scrollIntoView({ block: "start", behavior: "smooth" })
  }

  // Drag the divider: the pane's width is the distance from the pointer to the right edge.
  const onDividerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault()
    const move = (ev: PointerEvent) => {
      const pct = ((window.innerWidth - ev.clientX) / window.innerWidth) * 100
      onWidthPct(Math.min(MAX_PCT, Math.max(MIN_PCT, pct)))
    }
    const up = () => {
      window.removeEventListener("pointermove", move)
      window.removeEventListener("pointerup", up)
    }
    window.addEventListener("pointermove", move)
    window.addEventListener("pointerup", up)
  }, [onWidthPct])
  const onDividerKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") onWidthPct(Math.min(MAX_PCT, widthPct + 3))
    else if (e.key === "ArrowRight") onWidthPct(Math.max(MIN_PCT, widthPct - 3))
  }

  return (
    <>
      <aside
        id="osim-draft"
        className={`osim-pane${open ? " open" : ""}${wide ? "" : " is-full"}`}
        aria-label="Draft order"
        ref={local}
        style={wide ? ({ "--osim-pane-w": `${widthPct}vw` } as React.CSSProperties) : undefined}
      >
        {wide ? (
          <div
            className="osim-divider"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize draft pane"
            aria-valuemin={MIN_PCT}
            aria-valuemax={MAX_PCT}
            aria-valuenow={Math.round(widthPct)}
            tabIndex={0}
            onPointerDown={onDividerDown}
            onKeyDown={onDividerKey}
          />
        ) : null}
        <div className="osim-pane-head">
          {wide ? null : (
            <button type="button" className="scb osim-back" data-backtochat onClick={hide}>
              <ChevronLeft size={16} aria-hidden="true" /> Back to chat
            </button>
          )}
          <p className="lab">Draft order{draft ? ` · v${draft.version}` : ""}</p>
          {wide ? (
            <button className="ib" type="button" aria-label="Collapse draft" aria-expanded="true" aria-controls="osim-draft" onClick={hide}>
              <ChevronRight size={16} aria-hidden="true" />
            </button>
          ) : null}
        </div>
        <div className="osim-pane-scroll">
          <p className="osim-panel-banner" role="note">{SIM_BANNER_COPY}</p>
          <div className="osim-nav-tools">
            <p className="note">{drafted} of {ids.length} sections drafted</p>
            <button type="button" className="pbtn" disabled={drafted === 0 || exporting} onClick={() => void download()}>
              {exporting ? "Building…" : "Download DOCX"}
            </button>
          </div>
          {exportError ? <p className="note" role="alert">{exportError}</p> : null}
          {stale?.length ? (
            <p className="rsum" role="status">Out of date: {stale.join(", ")}. The simulator will redo these with your confirmation.</p>
          ) : null}
          <nav className="osim-outline" aria-label="Draft outline">
            {ids.map((id) => (
              <a key={id} href={`#osim-sec-${id}`} onClick={jump(id)} data-drafted={draft?.sections[id]?.markdown.trim() ? "1" : "0"}>
                {sectionLabel(id, ids, titles)}
              </a>
            ))}
          </nav>
          <DraftPaper threadId={threadId} title={title} />
        </div>
      </aside>
      {open ? null : (
        <button
          type="button"
          className={`osim-tab${unseen ? " has-new" : ""}`}
          ref={tab}
          aria-controls="osim-draft"
          aria-expanded="false"
          onClick={() => onOpenChange(true)}
        >
          Draft · {drafted}/{ids.length}
          {unseen ? <span className="osim-dot" role="status" aria-label="New draft changes" /> : null}
        </button>
      )}
    </>
  )
}

/** Export .docx: pending edits saved first, so the file matches the page. */
export function useDraftExport(threadId: string, title: string) {
  const facts = useSimStore((s) => s.threads[threadId]?.caseFacts ?? null)
  const factOffice = facts?.office?.trim() || undefined
  const factCNo = facts?.c_no?.trim() || undefined
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState("")
  const download = async () => {
    setExporting(true)
    setExportError("")
    try {
      // Pending edits are saved first so the file matches what is on screen.
      const outcome = await flushThreadEdits(threadId)
      if (outcome === "conflict" || outcome === "error") {
        setExportError("Some edits are not saved yet. Resolve them, then download again.")
        return
      }
      const latest = useSimStore.getState().threads[threadId]?.draft
      // Every section id is passed (empty ones too) so the numbering matches the page; empty ones are skipped in the file.
      const all = canonicalIds(Object.keys(latest?.sections ?? {}))
      if (!all.some((id) => latest?.sections[id]?.markdown.trim())) return
      await downloadOioSimDocx(
        all.map((id) => ({ id, markdown: latest?.sections[id]?.markdown ?? "" })),
        {
          title: title || "OIO draft",
          titles: latest?.titles,
          office: factOffice,
          cNo: factCNo,
          noticee: facts?.noticee_name?.trim() || undefined,
          date: todayDmy(),
        },
      )
      track("draft_exported")
    } catch {
      setExportError("Couldn't build the DOCX. Try again.")
    } finally {
      setExporting(false)
    }
  }
  return { download, exporting, exportError }
}

/**
 * The draft as one paper document: the caption, then every section in order as its own editor
 * (autosave, versions, conflicts and history unchanged). `onSection` lets a page tie its own
 * notes to a section as it renders.
 */
export function DraftPaper({ threadId, title, aside }: { threadId: string; title: string; aside?: (sectionId: string) => React.ReactNode }) {
  const facts = useSimStore((s) => s.threads[threadId]?.caseFacts ?? null)
  const factCNo = facts?.c_no?.trim() || undefined
  const { draft, streaming, ids } = useDraftSections(threadId)
  const titles = draft?.titles
  const headings = useMemo(() => sectionHeadings(ids, titles, (id) => !!draft?.sections[id]?.markdown.trim()), [ids, titles, draft])
  // One paragraph counter across the page; the editors stay unnumbered and the gutter draws the numbers.
  const numbering = useMemo(
    () => planNumbering(ids.map((id) => ({ id, markdown: stripLeadingHeading(draft?.sections[id]?.markdown ?? "") }))),
    [ids, draft],
  )
  const office = facts?.office?.trim() || "[Office]"
  const caseDate = useMemo(() => todayDmy(), [])
  return (
    <article className="osim-paper" aria-label="Draft order document">
      <header className="osim-caption" aria-label={title || "OIO draft"}>
        <p className="osim-letterhead">{office.toUpperCase()}</p>
        <p className="osim-cno"><span>C. No. {factCNo ?? "[C. No.]"}</span><span>Date: {caseDate}</span></p>
        <p className="osim-oio-no">ORDER-IN-ORIGINAL No. {CAPTION_PLACEHOLDERS[0]}</p>
        <p className="osim-passed">(Passed by [Name of Adjudicating Authority], [Designation], {office})</p>
        <p className="osim-din">DIN: {CAPTION_PLACEHOLDERS[1]}</p>
      </header>
      {ids.map((id, i) => {
        const group = headings[id]?.group
        const note = aside?.(id)
        return (
          <div key={id} id={`osim-sec-${id}`} className={`osim-doc-sec${note ? " has-note" : ""}`}>
            <SectionEditor
              key={`${threadId}:${id}`}
              threadId={threadId}
              sectionId={id}
              section={draft?.sections[id]}
              streaming={streaming?.sectionId === id ? streaming.markdown : null}
              heading={headings[id]}
              groupHeading={!!group && headings[ids[i - 1]]?.group !== group}
              ids={ids}
              titles={titles}
              numbers={numbering.get(id)}
            />
            {note ? <aside className="nsdr-margin">{note}</aside> : null}
          </div>
        )
      })}
    </article>
  )
}
